/* TTW Deep Current — decorative canvas engines (plankton + fish school).
   Structured like ttw-motion.js: idempotent init, reduced-motion early-return,
   IntersectionObserver-gated so off-screen canvases stop their rAF loop, and a
   page-wide cap so no more than N canvases animate at once. aria-hidden layers
   only; pointer-events:none is enforced in ttw-decor.css. */
(function () {
  'use strict';

  var REDUCE = false;
  try { REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var MAX_ACTIVE = 3;   // hard perf cap: max simultaneously-animating canvases per page
  var active = 0;       // module-wide counter shared across every engine

  function hexToRgb(h) {
    if (!h) return '27,111,168';
    h = ('' + h).replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return '27,111,168';
    return ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255);
  }

  function makeEngine(canvas) {
    var kind = canvas.getAttribute('data-ttw-fx') || 'plankton';
    var accent = hexToRgb(canvas.getAttribute('data-accent') || '#1B6FA8');
    var accent2 = hexToRgb(canvas.getAttribute('data-accent2') || '#0F6E56');
    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = 0, H = 0, items = [], raf = 0, running = false, built = false;

    function resize() {
      var r = canvas.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = Math.max(1, Math.round(W * dpr));
      canvas.height = Math.max(1, Math.round(H * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      if (!running) staticFrame();
    }

    function build() {
      items = [];
      if (!W || !H) { built = false; return; }
      if (kind === 'fish') {
        var n = Math.max(10, Math.min(26, Math.round((W * H) / 32000)));
        for (var i = 0; i < n; i++) {
          var dir = Math.random() < 0.5 ? -1 : 1;
          items.push({
            x: Math.random() * W,
            baseY: Math.random() * H,
            vx: dir * (0.18 + Math.random() * 0.24),
            amp: 4 + Math.random() * 8,
            phase: Math.random() * Math.PI * 2,
            ps: 0.008 + Math.random() * 0.012,
            size: 5 + Math.random() * 6,
            dir: dir,
            school: i % 2
          });
        }
      } else { // plankton — rising particles (adapted from ttw-partner.liquid)
        var m = Math.max(24, Math.min(64, Math.round((W * H) / 16000)));
        for (var j = 0; j < m; j++) {
          items.push({
            x: Math.random() * W, y: Math.random() * H,
            r: Math.random() * 2 + 0.6,
            vx: (Math.random() - 0.5) * 0.12,
            vy: -(Math.random() * 0.16 + 0.05),
            o: Math.random() * 0.045 + 0.015
          });
        }
      }
      built = true;
    }

    function drawFish(f, t) {
      var y = f.baseY + Math.sin(t * f.ps + f.phase) * f.amp;
      var rgb = f.school ? accent2 : accent;
      var s = f.size;
      ctx.save();
      ctx.translate(f.x, y);
      ctx.scale(f.dir, 1);
      ctx.fillStyle = 'rgba(' + rgb + ',0.16)';
      ctx.beginPath();
      ctx.ellipse(0, 0, s, s * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-s * 0.85, 0);
      ctx.lineTo(-s * 1.5, -s * 0.55);
      ctx.lineTo(-s * 1.5, s * 0.55);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function drawPlankton(p) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(' + accent + ',' + p.o + ')';
      ctx.fill();
    }

    function frame(ts) {
      if (!running) return;
      ctx.clearRect(0, 0, W, H);
      var i;
      if (kind === 'fish') {
        for (i = 0; i < items.length; i++) {
          var f = items[i];
          f.x += f.vx;
          if (f.dir > 0 && f.x - f.size * 1.6 > W) { f.x = -f.size * 1.6; f.baseY = Math.random() * H; }
          else if (f.dir < 0 && f.x + f.size * 1.6 < 0) { f.x = W + f.size * 1.6; f.baseY = Math.random() * H; }
          drawFish(f, ts || 0);
        }
      } else {
        for (i = 0; i < items.length; i++) {
          var p = items[i];
          p.x += p.vx; p.y += p.vy;
          if (p.y < -p.r) { p.y = H + p.r; p.x = Math.random() * W; }
          drawPlankton(p);
        }
      }
      raf = requestAnimationFrame(frame);
    }

    function staticFrame() {
      if (!built) return;
      ctx.clearRect(0, 0, W, H);
      var i;
      if (kind === 'fish') { for (i = 0; i < items.length; i++) drawFish(items[i], 0); }
      else { for (i = 0; i < items.length; i++) drawPlankton(items[i]); }
    }

    return {
      mount: function () {
        resize();
        try { new ResizeObserver(resize).observe(canvas); }
        catch (e) { window.addEventListener('resize', resize); }
      },
      start: function () {
        if (running) return;
        if (REDUCE || active >= MAX_ACTIVE) { staticFrame(); return; }
        active++; running = true;
        raf = requestAnimationFrame(frame);
      },
      stop: function () {
        if (!running) return;
        running = false;
        cancelAnimationFrame(raf);
        active = Math.max(0, active - 1);
      }
    };
  }

  function initAll() {
    var canvases = document.querySelectorAll('canvas[data-ttw-fx]');
    if (!canvases.length) return;
    var io = null;
    try {
      io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          var eng = e.target.__ttwFx;
          if (!eng) return;
          if (e.isIntersecting) eng.start(); else eng.stop();
        });
      }, { threshold: 0.01, rootMargin: '120px' });
    } catch (e) { io = null; }

    Array.prototype.forEach.call(canvases, function (c) {
      if (c.__ttwFx) return;
      var eng = makeEngine(c);
      c.__ttwFx = eng;
      eng.mount();
      if (REDUCE || !io) { eng.start(); return; } // static (reduced) or uncapped fallback
      io.observe(c);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();
})();
